/*
 * Resolved runtime config. No env vars (the no-env-vars rule): the Clerk key is a
 * baked constant tied to the Clerk INSTANCE, and the cloud URL is DERIVED from the
 * document origin. Mirrors app-ui/src/core/env.ts.
 */

/**
 * Clerk publishable key — PUBLIC (ships in every bundle), one production instance shared by every
 * app, so a session and its `pla`/`fea` claims are the same everywhere. A baked CONSTANT.
 *
 * THE KEY ENCODES THE HOST: `pk_live_<base64("clerk.<domain>$")>`. Two consequences worth keeping
 * in mind together, because the first invites the mistake the second punishes — a domain move
 * cannot be done by find-and-replace, and a hand-built key for a domain whose instance does not
 * exist yet is WELL-FORMED AND DEAD: every app fails to authenticate with nothing in the diff that
 * looks wrong. Copy the key Clerk gives you; never construct it.
 */
export const CLERK_PUBLISHABLE_KEY = 'pk_live_Y2xlcmsudml0ZXNzZWF1dG9tYXRpb24uYWkk';

const sameOrigin = (): string => globalThis.location?.origin ?? '';
// Sibling planes are DERIVED from whichever apex served this page — see @vitesse/base-ui/zones.
import { siblingUrl } from '@vitesse/base-ui/zones';

/*
 * Cloud control-plane api-gateway base URL — for the FleetControl service. It
 * ALWAYS targets the cloud api-gateway (the one front door, which reverse-proxies
 * FleetControl to vitesse-fleet-control). DERIVED from the origin, never configured:
 *   prod  @ fleet.<apex> → the cloud front door
 *   local @ anywhere else               → same-origin, so the dev server's vite proxy
 *           forwards `/vitesse.v1.FleetControl/*` to the cloud (avoids browser CORS /
 *           mixed-content; the Connect path is the routing key).
 */
export const CLOUD_API_GATEWAY_URL: string = siblingUrl('cloud') || sameOrigin();

/*
 * AI plane base URL — the ai-worker, a SEPARATE backend from the api-gateway
 * (Cloudflare, off the Hetzner core). Derived the same way, for the same reason:
 *   prod  @ fleet.<apex> → the prod AI worker, cross-origin (it sends CORS)
 *   anywhere else                       → same-origin, so the dev server's vite proxy
 *           forwards the AI plane to the local `wrangler dev` (see ../../ai-plane-proxy.ts)
 * Worker-safe: this module is imported from the AI web worker, where `self.location`
 * resolves to the app origin, so the derivation matches the main thread.
 */
/*
 * SAME-ORIGIN, ALWAYS. This used to resolve to the sibling ai.<apex> in prod, which
 * made prod the only environment where the AI plane was cross-origin — a shape dev
 * could not reproduce, so each hole in the worker's CORS config shipped as a
 * prod-only failure. public/_worker.js now forwards the AI-plane prefixes to the
 * worker from this origin, exactly as the Vite proxy does in dev, so there is no
 * cross-origin request left to preflight.
 */
export const AI_URL: string = sameOrigin();

/*
 * LAN-direct candidates — the edge's OWN api-gateway, reached on the customer LAN
 * via the FIXED per-NIC Plex names `<role>.<fp>.gateway.vitesseautomation.ai`
 * (Cloudflare A-records → the box's real LAN IP; the `*.<fp>...` wildcard cert
 * covers them → trusted TLS to a LAN IP, no warning). The URL contract is the ONE
 * shared copy in @vitesse/base-ui — the SAME mechanism the gateway app uses — so it
 * CANNOT drift between the two apps; re-exported here so `./env` stays this app's
 * single env surface. (Adopting the legs — the LAN-vs-cloud race — is fleet-client's
 * concern; cloud is the default + guaranteed path, a LAN leg is a latency upgrade.)
 */
// Import from the '/plex' SUBPATH, not the '@vitesse/base-ui' barrel: the barrel pulls
// in every Lit element (which touch `document` at module-eval), so any worker-reachable
// module that hits the barrel throws `document is not defined` and blanks the app. plex.ts
// is pure URL strings — Worker-safe. (Same rule the gateway app's ./env follows.)
export { EDGE_PLEX_DOMAIN, lanCandidates, type LanRole, type LanLeg } from '@vitesse/base-ui/plex';
